import React, {
  useEffect,
  useState,
  useMemo,
  useContext,
  useCallback,
} from 'react';
import { useRouteMatch } from 'react-router-dom';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import { autorun } from 'mobx';

import useComponentMounted from '../../../hooks/useComponentMounted';
import ExternalCoachContext from '../ExternalCoachContext/ExternalCoachContext';
import CoachMissingSettingsModal from '../../components/CoachMissingSettingsModal/CoachMissingSettingsModal';
import CoachHistory, {
  HistoryEventType,
  ContentURLsChecklistbyCoach,
  getOnboardingChecklistByCoach,
} from '../../Model/CoachHistory';
import CoachOnboarding from '../../Model/CoachOnboarding';
import pages, { pagesConfig } from '../../config/pages';
import {
  ConfigurationType,
  configurationPagesConfig,
} from '../../config/configurationPages';
import OnboardingCoachNotificationsContext, {
  initialValues,
  TOTAL_ONBOARDING_TASKS_NEW_COACH,
  TOTAL_ONBOARDING_TASKS_EXISTING_COACH,
} from './OnboardingCoachNotificationsContext';

import texts from './texts.json';

const OnboardingCoachNotificationsContextProvider = ({
  children,
}) => {
  const { params: { userId } } = useRouteMatch();

  const [missingSettingsActions, setMissingSettingsActions] = useState([]);
  const [coachHistoryCollection, setCoachHistoryCollection] = useState([]);
  const [coachOnboardingDoc, setCoachOnboardingDoc] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isReady, setIsReady] = useState(initialValues.isReady);
  const [currentUserId, setCurrentUserId] = useState(userId);

  const isComponentMountedRef = useComponentMounted();

  const {
    externalCoachDoc: {
      stripeAccountId,
    },
    coachDoc: {
      avatar,
      id: coachId,
      isEnrolledInISProgram,
    },
  } = useContext(ExternalCoachContext);

  useEffect(() => {
    if (currentUserId !== userId) {
      setIsReady(false);
    }
  }, [
    currentUserId,
    userId,
  ]);

  useEffect(() => {
    const init = async () => {
      if (isComponentMountedRef.current) {
        const [coachHistory, coachOnboarding] = await Promise.all([
          CoachHistory.getCoachHistoryById(coachId),
          CoachOnboarding.getCoachOnboardingByCoachId(coachId),
        ]);
        setCoachHistoryCollection(coachHistory);
        setCoachOnboardingDoc(coachOnboarding);
      }
    };

    if (!isReady) {
      init();
    }
  }, [
    userId,
    coachId,
    isReady,
    isComponentMountedRef,
  ]);

  // The model returns a new Date on every read, so hooks are keyed on the timestamp.
  const accountCreatedAtTime = coachHistoryCollection?.docs?.find(
    (doc) => doc.type === HistoryEventType.ACCOUNT_CREATED,
  )?.createdAt?.getTime();
  // The new-coach checklist steps that apply to this coach, which depend on when their account was created
  const newCoachChecklist = useMemo(
    () => getOnboardingChecklistByCoach(new Date(accountCreatedAtTime)),
    [accountCreatedAtTime],
  );

  const computeMissingSettings = useCallback(() => {
    if (!isComponentMountedRef.current) return [];
    const missingSettings = [];
    if (!stripeAccountId) {
      missingSettings.push({
        path: `${pagesConfig[pages.CONFIG].path}/${configurationPagesConfig[ConfigurationType.PAYMENT].path}`,
        label: texts.missingStripeAccountId,
        description: texts.missingStripeAccountIdDescription,
        buttonText: texts.missingStripeAccountIdButton,
        timeInMinutes: '5',
      });
    }

    if (!avatar) {
      missingSettings.push({
        path: `${pagesConfig[pages.CONFIG].path}/${configurationPagesConfig[ConfigurationType.CHECKOUT_PAGE].path}`,
        label: texts.missingAvatarUrl,
        description: texts.missingAvatarUrlDescription,
        buttonText: texts.missingAvatarUrlButton,
        timeInMinutes: '2',
      });
    }

    /*
    * Onbording checklist feature will be released on August 1st,2025
    * Before that date, we need to show the onboarding items
    */
    const augustFirst = new Date('2025-08-01T00:00:00.000Z');
    const hasHistoryEvent = (type) => !!coachHistoryCollection?.docs?.some((doc) => doc.type === type);

    if (accountCreatedAtTime > augustFirst.getTime()) {
      if (!!coachOnboardingDoc?.coachDriveFolder
        && !hasHistoryEvent(HistoryEventType.COACH_ONBOARDING_FORM_COMPLETED)) {
        missingSettings.push({
          historyItem: HistoryEventType.COACH_ONBOARDING_FORM_COMPLETED,
          path: coachOnboardingDoc?.coachDriveFolder,
          label: texts.missingCoachOnboardingForm,
          description: texts.missingCoachOnboardingFormDescription,
          buttonText: texts.missingCoachOnboardingFormButton,
          timeInMinutes: '15',
        });
      }

      if (!hasHistoryEvent(HistoryEventType.SOCIAL_HANDLES_ADDED)) {
        missingSettings.push({
          historyItem: HistoryEventType.SOCIAL_HANDLES_ADDED,
          label: texts.missingSocialHandles,
          description: texts.missingSocialHandlesDescription,
          buttonText: texts.missingSocialHandlesButton,
          timeInMinutes: '2',
        });
      }

      if (!hasHistoryEvent(HistoryEventType.IS_AC_QUESTIONNAIRE_SUBMITTED) && !isEnrolledInISProgram) {
        missingSettings.push({
          historyItem: HistoryEventType.IS_AC_QUESTIONNAIRE_SUBMITTED,
          label: texts.missingIsAcQuestionnaire,
          description: texts.missingIsAcQuestionnaireDescription,
          buttonText: texts.missingIsAcQuestionnaireButton,
          timeInMinutes: '3-5',
        });
      }

      // Check if all required content URL types are present
      const contentURLDocs = coachHistoryCollection?.docs?.filter(
        (doc) => ContentURLsChecklistbyCoach.includes(doc.type),
      ) || [];

      // Get unique types that have been submitted
      const submittedTypes = [...new Set(contentURLDocs.map((doc) => doc.type))];
      const completedContentURLs = submittedTypes.length;

      const totalContentURLs = ContentURLsChecklistbyCoach.length;

      if (completedContentURLs < totalContentURLs) {
        missingSettings.push({
          historyItem: HistoryEventType.CONTENT_URLS_SUBMITTED,
          label: `${texts.missingContentURLs} (${completedContentURLs}/${totalContentURLs})`,
          description: texts.missingContentURLsDescription,
          buttonText: texts.missingContentURLsButton,
          timeInMinutes: '2-3',
        });
      }

      if (!hasHistoryEvent(HistoryEventType.GOOGLE_CALENDAR_CREATED)) {
        missingSettings.push({
          historyItem: HistoryEventType.GOOGLE_CALENDAR_CREATED,
          label: texts.missingGoogleCalendar,
          description: texts.missingGoogleCalendarDescription,
          buttonText: texts.missingGoogleCalendarButton,
          timeInMinutes: '2',
        });
      }

      // Completed by the server once the coach writes in their Avida coaching channel. Only asked of accounts
      // created since the step launched: older coaches already talk to their account manager in the chat.
      if (newCoachChecklist.includes(HistoryEventType.FIRST_CHAT_MESSAGE_SENT)
        && !hasHistoryEvent(HistoryEventType.FIRST_CHAT_MESSAGE_SENT)) {
        missingSettings.push({
          historyItem: HistoryEventType.FIRST_CHAT_MESSAGE_SENT,
          path: pagesConfig[pages.CHAT].path,
          label: texts.missingFirstChatMessage,
          description: texts.missingFirstChatMessageDescription,
          buttonText: texts.missingFirstChatMessageButton,
          timeInMinutes: '1',
        });
      }
    }

    return missingSettings;
  }, [
    isComponentMountedRef,
    stripeAccountId,
    avatar,
    coachHistoryCollection,
    isEnrolledInISProgram,
    coachOnboardingDoc,
    accountCreatedAtTime,
    newCoachChecklist,
  ]);

  useEffect(() => {
    // Only run when we have the coach history collection loaded
    if (!isReady && coachHistoryCollection && coachHistoryCollection.docs) {
      const updated = computeMissingSettings();
      if (updated.length > 0) {
        setShowModal(true);
      }
      setMissingSettingsActions(updated);
      setIsReady(true);
      setCurrentUserId(userId);
    }
  }, [
    userId,
    currentUserId,
    isComponentMountedRef,
    avatar,
    stripeAccountId,
    isReady,
    coachHistoryCollection,
    isEnrolledInISProgram,
    showModal,
    computeMissingSettings,
  ]);

  // update missing settings actions when coachHistoryCollection changes
  useEffect(() => {
    const disposer = autorun(() => {
      if (coachHistoryCollection) {
        const updated = computeMissingSettings();
        setMissingSettingsActions(updated);
      }
    });
    return () => disposer();
  }, [coachHistoryCollection, computeMissingSettings]);

  const contextValue = useMemo(() => ({
    isReady,
    missingSettingsActions,
    showModal,
    setShowModal,
    TOTAL_ONBOARDING_TASKS_NEW_COACH,
    TOTAL_ONBOARDING_TASKS_EXISTING_COACH,
    newCoachTaskCount: newCoachChecklist.length,
  }), [
    newCoachChecklist,
    isReady,
    missingSettingsActions,
    showModal,
    setShowModal,
  ]);

  return (
    <OnboardingCoachNotificationsContext.Provider value={contextValue}>
      {!!missingSettingsActions.length && (
        <CoachMissingSettingsModal
          missingSettings={missingSettingsActions}
          isOpen={showModal}
          onClose={() => setShowModal(false)}
        />
      )}
      {children}
    </OnboardingCoachNotificationsContext.Provider>
  );
};

OnboardingCoachNotificationsContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default compose(
  observer,
)(OnboardingCoachNotificationsContextProvider);
